import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, PartyPopper, Copy } from "lucide-react";
import { loadImage, baseCanvas, goldGradient, drawGlow, drawQrCard } from "@/components/InvitePoster";

const TEMPLATES = [
  { id: "blackgold", name: "黑金喜报" },
  { id: "redgold", name: "红金喜报" },
];

const CAPTIONS = [
  (d) =>
    `我的项目《${d.title}》被合赢项目交流社区录用了，奖励 ¥${d.reward.toLocaleString()} 已到账，可提现 USDT！平台每天分享日赚500+稳定项目，投稿录用奖励最高 ¥188,888。想一起赚的朋友扫海报二维码，注册填我邀请码 ${d.inviteCode}，各得奖励！`,
  (d) =>
    `晒一下到账喜报：投稿项目《${d.title}》通过合赢项目交流社区审核，¥${d.reward.toLocaleString()} 奖励秒到账。这里有专业团队把关的靠谱项目，激活会员天天分享日赚500+项目。扫码加入，邀请码 ${d.inviteCode}，投稿录用奖励最高 ¥188,888！`,
  (d) =>
    `又一笔到账！在合赢项目交流社区投稿的《${d.title}》被录用上架，¥${d.reward.toLocaleString()} 直接进余额。零风险项目社区，投稿有奖、邀请有奖（1人¥128）。扫海报二维码注册，填邀请码 ${d.inviteCode} 绑定奖励，一起搞钱！`,
];

const truncate = (t, n) => (t.length > n ? `${t.slice(0, n)}…` : t);

/* ---------- 装饰件 ---------- */

export function drawDiamond(ctx, cx, cy, s, color) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx, cy - s);
  ctx.lineTo(cx + s, cy);
  ctx.lineTo(cx, cy + s);
  ctx.lineTo(cx - s, cy);
  ctx.closePath();
  ctx.fill();
}

export function drawCornerFrame(ctx, color) {
  const inset = 40;
  const len = 96;
  const W = 900;
  const H = 1500;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  const corners = [
    [inset, inset, 1, 1],
    [W - inset, inset, -1, 1],
    [inset, H - inset, 1, -1],
    [W - inset, H - inset, -1, -1],
  ];
  corners.forEach(([x, y, dx, dy]) => {
    ctx.beginPath();
    ctx.moveTo(x + dx * len, y);
    ctx.lineTo(x, y);
    ctx.lineTo(x, y + dy * len);
    ctx.stroke();
    drawDiamond(ctx, x + dx * 8, y + dy * 8, 5, color);
  });
  ctx.strokeStyle = color.replace(/[\d.]+\)$/, "0.14)");
  ctx.lineWidth = 1;
  ctx.strokeRect(inset + 18, inset + 18, W - (inset + 18) * 2, H - (inset + 18) * 2);
}

export function drawVerticalText(ctx, text, x, y, gap, font, color) {
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  [...text].forEach((ch, i) => ctx.fillText(ch, x, y + i * gap));
}

export function drawHairRule(ctx, y, half, color) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(450 - half, y);
  ctx.lineTo(450 - 26, y);
  ctx.moveTo(450 + 26, y);
  ctx.lineTo(450 + half, y);
  ctx.stroke();
  drawDiamond(ctx, 450, y, 6, color);
  drawDiamond(ctx, 450 - half, y, 4, color);
  drawDiamond(ctx, 450 + half, y, 4, color);
}

export function drawConfetti(ctx, palette) {
  for (let i = 0; i < 46; i++) {
    const x = 70 + Math.random() * 760;
    const y = 70 + Math.random() * 1360;
    const s = 2 + Math.random() * 4.5;
    const c = palette[Math.floor(Math.random() * palette.length)];
    ctx.globalAlpha = 0.05 + Math.random() * 0.14;
    if (Math.random() > 0.5) {
      drawDiamond(ctx, x, y, s, c);
    } else {
      ctx.fillStyle = c;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.random() * Math.PI);
      ctx.fillRect(-s, -s * 0.35, s * 2, s * 0.7);
      ctx.restore();
    }
  }
  ctx.globalAlpha = 1;
}

export function drawSeal(ctx, cx, cy, color, textColor) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  const s = 46;
  ctx.beginPath();
  ctx.roundRect(cx - s, cy - s, s * 2, s * 2, 10);
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(cx - s + 7, cy - s + 7, s * 2 - 14, s * 2 - 14, 6);
  ctx.stroke();
  ctx.fillStyle = textColor;
  ctx.font = "900 30px 'Noto Serif SC', serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("合赢", cx, cy - 16);
  ctx.fillText("甄选", cx, cy + 18);
  ctx.textBaseline = "alphabetic";
  ctx.restore();
}

/* ---------- 版面 ---------- */

export function drawHeader(ctx, gold, subColor) {
  ctx.textAlign = "center";
  drawDiamond(ctx, 450, 122, 9, gold);
  try { ctx.letterSpacing = "10px"; } catch (e) { /* noop */ }
  ctx.fillStyle = gold;
  ctx.font = "900 54px 'Noto Serif SC', serif";
  ctx.fillText("合赢项目交流社区", 450, 190);
  try { ctx.letterSpacing = "5px"; } catch (e) { /* noop */ }
  ctx.fillStyle = subColor;
  ctx.font = "17px 'JetBrains Mono', monospace";
  ctx.fillText("HEYING PROJECT EXCHANGE COMMUNITY", 450, 228);
  try { ctx.letterSpacing = "0px"; } catch (e) { /* noop */ }
}

function drawBody(ctx, gold, colors, data) {
  const { title, reward, username, date } = data;
  ctx.textAlign = "center";
  // 竖排侧题
  drawVerticalText(ctx, "投稿录用", 96, 400, 58, "700 30px 'Noto Serif SC', serif", colors.side);
  drawVerticalText(ctx, "喜报频传", 804, 400, 58, "700 30px 'Noto Serif SC', serif", colors.side);
  // 喜报（叠影层次）
  ctx.font = "900 226px 'Noto Serif SC', serif";
  ctx.fillStyle = colors.shadow;
  ctx.fillText("喜报", 454, 516);
  ctx.fillStyle = gold;
  ctx.fillText("喜报", 450, 510);
  try { ctx.letterSpacing = "6px"; } catch (e) { /* noop */ }
  ctx.fillStyle = colors.text;
  ctx.font = "30px 'Noto Sans SC', sans-serif";
  ctx.fillText("会员投稿 · 通过平台审核评估", 450, 612);
  try { ctx.letterSpacing = "0px"; } catch (e) { /* noop */ }
  ctx.fillStyle = gold;
  ctx.font = "700 42px 'Noto Serif SC', serif";
  ctx.fillText(`《${truncate(title, 13)}》`, 450, 690);
  // 金额
  try { ctx.letterSpacing = "4px"; } catch (e) { /* noop */ }
  ctx.fillStyle = colors.dim;
  ctx.font = "18px 'JetBrains Mono', monospace";
  ctx.fillText("REWARD RECEIVED · 录用奖励已到账", 450, 766);
  try { ctx.letterSpacing = "0px"; } catch (e) { /* noop */ }
  ctx.font = "900 124px 'JetBrains Mono', monospace";
  ctx.fillStyle = colors.shadow;
  ctx.fillText(`+¥${reward.toLocaleString()}`, 454, 916);
  ctx.fillStyle = gold;
  ctx.fillText(`+¥${reward.toLocaleString()}`, 450, 910);
  drawHairRule(ctx, 956, 210, colors.rule);
  ctx.fillStyle = colors.text;
  ctx.font = "25px 'Noto Sans SC', sans-serif";
  ctx.fillText("已到账 · 可提现 USDT · 项目已上架「今日项目」", 450, 1012);
  ctx.fillStyle = colors.dim;
  ctx.font = "22px 'Noto Sans SC', sans-serif";
  ctx.fillText(`会员 ${username} · ${date}`, 450, 1062);
}

export function drawShare(ctx, qrImg, gold, dimColor, inviteCode) {
  drawQrCard(ctx, qrImg, 450, 1210, 196);
  ctx.textAlign = "center";
  ctx.fillStyle = gold;
  ctx.font = "700 27px 'Noto Serif SC', serif";
  ctx.fillText("扫码加入 · 投稿录用奖励最高 ¥188,888", 450, 1368);
  ctx.fillStyle = dimColor;
  ctx.font = "20px 'Noto Sans SC', sans-serif";
  ctx.fillText(`我的邀请码 ${inviteCode} · 注册填写各得奖励`, 450, 1412);
}

function drawBlackGold(ctx, qrImg, data) {
  const gold = goldGradient(ctx);
  drawGlow(ctx, 470);
  drawConfetti(ctx, ["#D4AF37", "#F3E5AB", "#AA7C11"]);
  drawCornerFrame(ctx, "rgba(212,175,55,0.55)");
  drawHeader(ctx, gold, "#7c818c");
  drawSeal(ctx, 764, 140, "rgba(212,175,55,0.75)", "#D4AF37");
  drawBody(ctx, gold, {
    text: "#F8F9FA",
    dim: "#6B7280",
    side: "rgba(212,175,55,0.4)",
    rule: "rgba(212,175,55,0.5)",
    shadow: "rgba(122,92,16,0.55)",
  }, data);
  drawShare(ctx, qrImg, gold, "#6B7280", data.inviteCode);
  ctx.fillStyle = "#4B5563";
  ctx.font = "19px 'Noto Sans SC', sans-serif";
  ctx.fillText("合赢项目交流社区 · 可靠零风险的赚钱项目", 450, 1452);
}

function drawRedGold(ctx, qrImg, data) {
  const bg = ctx.createLinearGradient(0, 0, 0, 1500);
  bg.addColorStop(0, "#8E1414");
  bg.addColorStop(0.5, "#B42222");
  bg.addColorStop(1, "#701010");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, 900, 1500);
  // 同心环纸感肌理
  ctx.strokeStyle = "rgba(243,229,171,0.05)";
  ctx.lineWidth = 1;
  for (let r = 140; r <= 860; r += 90) {
    ctx.beginPath();
    ctx.arc(450, 520, r, 0, Math.PI * 2);
    ctx.stroke();
  }
  drawGlow(ctx, 470, "243,229,171");
  drawConfetti(ctx, ["#F3E5AB", "#FFD98A", "#D4AF37"]);
  drawCornerFrame(ctx, "rgba(243,229,171,0.6)");
  const gold = goldGradient(ctx);
  drawHeader(ctx, gold, "rgba(255,235,200,0.65)");
  drawSeal(ctx, 764, 140, "rgba(243,229,171,0.8)", "#F3E5AB");
  drawBody(ctx, gold, {
    text: "#FFF7E6",
    dim: "rgba(255,235,200,0.7)",
    side: "rgba(243,229,171,0.45)",
    rule: "rgba(243,229,171,0.55)",
    shadow: "rgba(80,8,8,0.5)",
  }, data);
  drawShare(ctx, qrImg, gold, "rgba(255,235,200,0.7)", data.inviteCode);
  ctx.fillStyle = "rgba(255,235,200,0.5)";
  ctx.font = "19px 'Noto Sans SC', sans-serif";
  ctx.fillText("合赢项目交流社区 · 可靠零风险的赚钱项目", 450, 1452);
}

export default function RewardPoster({ open, onClose, submission, username, inviteCode }) {
  const [tpl, setTpl] = useState("blackgold");
  const [imgUrl, setImgUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");

  const buildCaption = () => {
    const data = {
      title: submission?.title || "优质项目",
      reward: submission?.reward || 188888,
      inviteCode,
    };
    return CAPTIONS[Math.floor(Math.random() * CAPTIONS.length)](data);
  };

  const copyCaption = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (open && submission) {
      setImgUrl(null);
      setCaption("");
      generate(tpl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tpl, submission]);

  const generate = async (template) => {
    setBusy(true);
    try {
      await document.fonts.ready;
      const inviteLink = `${window.location.origin}/?ref=${inviteCode}`;
      const qrData = await QRCode.toDataURL(inviteLink, {
        width: 600,
        margin: 0,
        color: { dark: "#0A0A0E", light: "#FFFFFF" },
      });
      const qrImg = await loadImage(qrData);
      const { canvas, ctx } = baseCanvas();
      const data = {
        title: submission.title,
        reward: submission.reward || 188888,
        username,
        date: (submission.reviewed_at || submission.created_at || "").slice(0, 10),
        inviteCode,
      };
      if (template === "redgold") drawRedGold(ctx, qrImg, data);
      else drawBlackGold(ctx, qrImg, data);
      setImgUrl(canvas.toDataURL("image/png"));
    } catch (e) {
      toast.error("喜报生成失败，请重试");
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (!imgUrl) return;
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = `合赢录用喜报-${tpl}.png`;
    a.click();
    const text = buildCaption();
    setCaption(text);
    const copied = await copyCaption(text);
    if (copied) {
      toast.success("喜报已保存，朋友圈文案已复制，直接粘贴即可发布");
    } else {
      toast.warning("喜报已保存，请长按下方文案手动复制");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-[#0F0F14] border border-[#D4AF37]/25 text-[#F8F9FA] sm:max-w-md"
        data-testid="reward-poster-dialog"
      >
        <DialogTitle className="font-serif-sc text-xl tracking-widest text-center flex items-center justify-center gap-2">
          <PartyPopper className="w-5 h-5 text-[#D4AF37]" /> 录用喜报海报
        </DialogTitle>
        <DialogDescription className="sr-only">投稿录用喜报海报，含到账金额与专属邀请二维码</DialogDescription>
        <p className="text-xs text-gray-500 text-center">
          喜报内含您的专属邀请二维码，转发朋友圈，好友扫码注册奖励自动归您
        </p>
        <div className="grid grid-cols-2 gap-2">
          {TEMPLATES.map((t) => (
            <button
              key={t.id}
              data-testid={`reward-poster-tpl-${t.id}`}
              onClick={() => setTpl(t.id)}
              className={`py-2 text-xs tracking-widest border transition-colors ${
                tpl === t.id ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-[#D4AF37]/15 text-gray-500 hover:text-gray-300"
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>
        <div className="grid place-items-center py-2 min-h-40">
          {busy && <p className="text-sm text-gray-500 py-20">喜报绘制中...</p>}
          {!busy && imgUrl && (
            <img
              src={imgUrl}
              data-testid="reward-poster-image"
              alt="合赢录用喜报"
              className="w-full max-w-[300px] border border-[#D4AF37]/25"
            />
          )}
        </div>
        <button
          data-testid="reward-poster-download-btn"
          onClick={download}
          disabled={!imgUrl}
          className="btn-gold py-2.5 text-sm w-full inline-flex items-center justify-center gap-2"
        >
          <Download className="w-4 h-4" /> 保存喜报并复制朋友圈文案
        </button>
        {caption && (
          <div className="border border-[#D4AF37]/20 bg-[#0A0A0E] p-3" data-testid="reward-caption-box">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] tracking-widest text-gray-500">朋友圈文案（已自动复制）</span>
              <button
                data-testid="reward-caption-copy-btn"
                onClick={async () => {
                  const ok = await copyCaption(caption);
                  if (ok) toast.success("文案已复制");
                  else toast.error("复制失败，请长按手动复制");
                }}
                className="text-[10px] text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1 inline-flex items-center gap-1 hover:bg-[#D4AF37]/10 transition-colors"
              >
                <Copy className="w-3 h-3" /> 重新复制
              </button>
            </div>
            <p className="text-xs text-gray-300 leading-relaxed select-text" data-testid="reward-caption-text">{caption}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
